import {Dialog} from '@material-ui/core';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';
import Timeago from 'react-timeago';
import {toast} from 'react-toastify';

import * as Analytics from '../../services/analytics';
import {useDispatch} from '../../state/hooks';
import * as ReportActions from '../../state/reports/actions';
import * as ReportThunks from '../../state/reports/thunks';
import {ApolloClient} from '../../state/types';
import {useViewer} from '../../state/viewer/hooks';
import * as ViewApi from '../../state/views/api';
import {ViewDraft} from '../../state/views/graphql';
import {ViewReducerState} from '../../state/views/reducerSupport';
import {LoadableView} from '../../state/views/types';
import {useNavigateToNewDraft} from './hooks';
import * as S from './ReportDraftModals.styles';
import {getDraftUrl} from './utils';

type DraftReportWarningModalProps = {
  client: ApolloClient;
  view: LoadableView;
  views: ViewReducerState;
  entityName: string;
  projectName?: string;
  draft: ViewDraft;
  onModalClose: () => void;
};

// Note: this is a newer draft report warning modal that we should try to start using
export const DraftReportWarningModal = ({
  client,
  view,
  views,
  entityName,
  projectName,
  draft,
  onModalClose,
}: DraftReportWarningModalProps) => {
  const viewer = useViewer();
  const history = useHistory();
  const navigateToNewDraft = useNavigateToNewDraft({
    view,
    views,
    entityName,
    projectName,
  });

  const [isOpen, setIsOpen] = useState(true);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const onClose = () => {
    setIsOpen(false);
    onModalClose();
  };

  const onContinueClick = () => {
    Analytics.reportDraftWarningAction({
      actionClicked: 'continue',
    });

    const loadStartAction = ReportActions.loadStarted(draft.id);
    dispatch(ReportThunks.load(loadStartAction));

    setIsLoading(true);
    const draftUrl = getDraftUrl({
      parentId: view.id ?? '',
      draftId: draft.id,
      draftDisplayName: draft.displayName,
      entityName,
      projectName,
    });
    history.push(draftUrl);
    onClose();
    setIsLoading(false);
  };

  const onDiscardDraft = async () => {
    Analytics.reportDraftWarningAction({
      actionClicked: 'discard',
    });

    setIsDiscarding(true);
    const result = await ViewApi.deleteView(client, draft.id);
    setIsDiscarding(false);

    if (!result.data?.deleteView?.success || view.partRef == null) {
      toast('Error discarding draft. Please refresh and try again.');
      return;
    }
    navigateToNewDraft();
    onClose();
  };

  // Determine who last edited this report
  const updatedBy = view?.updatedBy;
  const isUpdatedByViewer = viewer?.id === updatedBy?.id;
  const lastUpdatedName = updatedBy?.name ?? updatedBy?.username;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <S.DialogTitle>Continue editing draft?</S.DialogTitle>
      <S.DialogContent>
        {view == null || lastUpdatedName == null ? (
          <p>
            Someone made an unpublished draft of this report. Would you like to
            continue editing the draft?
          </p>
        ) : (
          <p>
            {isUpdatedByViewer ? 'You' : lastUpdatedName} made an unpublished
            draft of this report{' '}
            {<Timeago date={view?.updatedAt} live={false} />}. Would you like to
            continue editing the draft?
          </p>
        )}
      </S.DialogContent>
      <S.DialogActions $align="left">
        <Button onClick={onContinueClick}>
          {isLoading ? 'Loading...' : 'Continue'}
        </Button>
        <Button variant="ghost" onClick={onDiscardDraft}>
          {isDiscarding ? 'Discarding...' : 'Discard draft'}
        </Button>
      </S.DialogActions>
    </Dialog>
  );
};
